import { useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { addDays, format } from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Alert, ModalButtonWithForm, TargetSchedule } from 'components/common';
import { Checkbox } from 'components/common/form';

import type { Course, Group, GroupCourse } from 'shared-types';
import type { UpdateGroupCourseUserSettingsReq } from 'shared-types/requests';

interface Props {
  course: Pick<
    Course,
    | 'id'
    | 'deadlineDate'
    | 'deadlineDurationUnit'
    | 'deadlineDurationValue'
    | 'deadlineType'
  >;
  group: Pick<Group, 'id' | 'title'> &
    Pick<
      GroupCourse,
      | 'deadlineDate'
      | 'deadlineDurationUnit'
      | 'deadlineDurationValue'
      | 'deadlineType'
      | 'isMandatory'
    >;
  isCourseMandatory: Course['isMandatory'];
  refresh: () => Promise<unknown>;
}

const ShareGroupsTargetSchedule = ({
  course,
  group,
  isCourseMandatory,
  refresh
}: Props) => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { timeZone }
  } = useReduxState();

  const [hasUsersWithSettings, setHasUsersWithSettings] = useState<string[]>(
    []
  );

  // the group's own setting can't change anything while the course is
  // mandatory for everyone
  const mandatoryDisabledReasons = isCourseMandatory
    ? [messages.courseMandatoryReasonCourse]
    : [];

  const getExistingUserSettings = async () => {
    try {
      const response = await api.get(
        `admin/courses/${course.id}/users-course-settings?groupId=${group.id}`
      );

      const { settingsByUserId } = response.data;
      setHasUsersWithSettings(Object.keys(settingsByUserId));
    } catch {
      // do nothing
    }
  };

  const handleSubmit = async ({
    isMandatory,
    resetCustomSchedules,
    ...scheduleValues
  }: typeof initialValues) => {
    try {
      const deadlineDate = scheduleValues.deadlineDate
        ? toDate(`${format(scheduleValues.deadlineDate, 'y-MM-dd')}T23:59:59`, {
            timeZone
          }).toISOString()
        : null;

      await api.put(`/admin/groups/${group.id}/courses/${course.id}`, {
        ...scheduleValues,
        resetCustomSchedules:
          hasUsersWithSettings.length > 0 && resetCustomSchedules,
        deadlineDate,
        ...(mandatoryDisabledReasons.length ? {} : { isMandatory })
      } satisfies UpdateGroupCourseUserSettingsReq);

      await refresh();

      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const initialValues = {
    deadlineDate: group.deadlineDate
      ? Date.parse(toZonedTime(group.deadlineDate, timeZone).toString())
      : Date.parse(addDays(now, 1).toString()),
    deadlineDurationUnit: group.deadlineDurationUnit || 'days',
    deadlineDurationValue: group.deadlineDurationValue || 7,
    deadlineType: group.deadlineType || ('course-default' as const),
    isMandatory: isCourseMandatory || group.isMandatory,
    resetCustomSchedules: false
  };

  return (
    <ModalButtonWithForm
      buttonIcon={['far', 'calendar-alt']}
      buttonIsDropdown={true}
      buttonText={messages.courseMandatoryAndTargetScheduleSet}
      initialValues={initialValues}
      modalActionText={messages.commonSubmit}
      modalTitle={messages.courseMandatoryAndTargetScheduleSet}
      modalWidth="medium"
      onSubmit={handleSubmit}
      onShow={getExistingUserSettings}
    >
      {() => (
        <div>
          <TargetSchedule
            settings={course}
            includeCourseDefaultOption={true}
            includeCourseDefaultValue={false}
            includeMandatoryOption={true}
            label={messages.courseTargetSchedule}
            helpText={messages.groupsUsersCourseTargetScheduleGroupInfo}
            mandatoryDisabledReasons={mandatoryDisabledReasons}
          />

          {hasUsersWithSettings.length ? (
            <Alert
              style={{ marginTop: 25 }}
              text={
                <Checkbox
                  field="resetCustomSchedules"
                  id={`reset-custom-schedules-${group.id}`}
                  label={messages.groupsUsersCourseTargetScheduleResetAction}
                />
              }
              title={getMessageWithPlaceholders({
                message: messages.groupsUsersCourseTargetScheduleResetInfo,
                placeholders: {
                  numberOfUsers: `${hasUsersWithSettings.length}`
                }
              })}
              titleStyle={{ fontWeight: 600 }}
              type="warning-light"
            />
          ) : null}
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default ShareGroupsTargetSchedule;
